import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Phone, Car, Eye, Gauge, Sparkles, TrendingUp, Copy, Lock, BarChart3, Users, Pin } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { VerifiedUnavailable } from "@/components/VerifiedUnavailable";
import { FreshnessNote } from "@/components/FreshnessNote";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAnalyst } from "@/context/AnalystContext";
import { addRecent } from "@/lib/recentListings";
import InvestmentValuation from "@/components/InvestmentValuation";
import WatchlistButton from "@/components/WatchlistButton";
import CopyStat from "@/components/CopyStat";
import { copyText } from "@/lib/clipboard";
import { bandForPrice } from "@/lib/bands";

const fmt = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
const fmtM = (n) => (n == null ? "—" : n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}K` : `$${Number(n).toLocaleString()}`);
const scoreColor = (s) => (s >= 70 ? "text-emerald-500" : s >= 50 ? "text-accent" : s >= 35 ? "text-amber-500" : "text-red-500");

const Stat = ({ label, value }) => (
  <div className="metric-empty p-4">
    <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-mono-data text-sm mt-1">{value ?? "—"}</div>
  </div>
);

const METRIC_LABELS = {
  band_median_price: "Band Median", price_vs_median_pct: "Price vs. Band Median",
  price_vs_msrp_pct: "Price vs. MSRP", band_median_miles: "Band Median Miles", days_on_market: "Days on Market",
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setContext, ask, pinToSlot } = useAnalyst();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);
  const [val, setVal] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendLocked, setTrendLocked] = useState(false);

  useEffect(() => {
    setLoading(true); setErr(false);
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!v) return;
    addRecent("vehicle", {
      id: v.id, title: v.heading || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim(),
      subtitle: [v.year, v.make, v.trim].filter(Boolean).join(" · "),
      price: v.price, photo: v.primary_image || (v.photos || [])[0] || null,
    });
    api.post("/activity/view", { item_type: "vehicle", item_id: v.id }).catch(() => {});
    api.post("/vehicles/valuation", { vehicle: v }).then((r) => setVal(r.data)).catch((e) => setVal(e?.response?.status === 402 ? { locked: true } : { available: false }));
    if (v.make) api.get("/market/history/vehicles", { params: { make: v.make } }).then((r) => setTrend(r.data.points)).catch((e) => e?.response?.status === 402 ? setTrendLocked(true) : setTrend([]));
    setContext({
      item_id: v.id, item_type: "vehicle", title: v.heading || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim(),
      type: "vehicle", heading: v.heading, year: v.year, make: v.make, model: v.model, trim: v.trim,
      price: v.price, msrp: v.msrp, price_difference_pct: v.price_difference_pct, miles: v.miles,
      days_on_market: v.days_on_market, engine: v.engine, transmission: v.transmission,
      drivetrain: v.drivetrain, vin: v.vin, dealer: v.dealer,
    });
    return () => setContext(null);
  }, [v, setContext]);

  useEffect(() => {
    if (val && !val.locked && val.available) {
      setContext((prev) => ({
        ...(prev || {}),
        investment_valuation: { score: val.investment_score, verdict: val.verdict, band: val.band, metrics: val.metrics, factors: val.factors },
        market_intelligence_band: val.band_context,
      }));
    }
  }, [val, setContext]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading verified listing…</div>;
  if (err || !v) return <div className="max-w-3xl mx-auto px-5 py-20"><VerifiedUnavailable note="This vehicle could not be retrieved from the verified source." /></div>;

  const photos = v.photos || [];
  const dealer = v.dealer || {};
  const title = v.heading || `${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim();
  const summary = [
    title,
    v.price ? `$${Number(v.price).toLocaleString()}` : null,
    v.vin ? `VIN ${v.vin}` : null,
  ].filter(Boolean).join("\n");
  const vBand = bandForPrice("veh", v.price);
  const marketUrl = vBand && v.make && v.model
    ? `/market?tab=vehicles&make=${encodeURIComponent(v.make)}&model=${encodeURIComponent(v.model)}&band=${vBand.key}`
    : null;
  const trendData = (trend || []).filter((p) => p.median_price).map((p) => ({ date: p.date, price: p.median_price }));

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
      <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to results
      </button>

      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          {photos.length ? (
            <div data-testid="vehicle-gallery">
              <motion.img key={active} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} src={photos[active]} alt={title} className="w-full h-[320px] sm:h-[460px] object-cover rounded-2xl" />
              {photos.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  {photos.slice(0, 12).map((p, i) => (
                    <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`} className={`shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${i === active ? "border-accent" : "border-transparent"}`}>
                      <img src={p} alt="" className="h-16 w-24 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-[320px] sm:h-[460px] metric-empty flex flex-col items-center justify-center gap-2 rounded-2xl" data-testid="vehicle-photo-placeholder">
              <Car className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
              <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-muted-foreground">No verified photo available</p>
            </div>
          )}

          <div className="mt-8">
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <MapPin className="w-4 h-4" /> {[dealer.city, dealer.state].filter(Boolean).join(", ") || "—"}
              <span className="ml-2 font-mono-data text-[10px] uppercase tracking-wider text-emerald-500 border border-emerald-500/30 rounded-full px-2 py-0.5">Verified</span>
            </div>
            <h1 className="font-editorial text-4xl sm:text-5xl italic leading-tight" data-testid="vehicle-title">{title}</h1>
            <div className="flex items-baseline gap-3 mt-4">
              <div className="font-mono-data text-3xl" data-testid="vehicle-price">{fmt(v.price)}</div>
              {v.price_difference_pct != null && (
                <span className={`font-mono-data text-sm ${v.price_difference_pct < 0 ? "text-emerald-500" : "text-muted-foreground"}`}>
                  {v.price_difference_pct > 0 ? "+" : ""}{v.price_difference_pct}% vs MSRP
                </span>
              )}
            </div>
            <FreshnessNote lastSeen={v._last_seen} className="mt-3" />
            <button
              onClick={() => ask?.(`Is this vehicle fairly priced? ${title}`, { item_type: "vehicle", item_id: v.id })}
              data-testid="ask-ai-about-listing"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 text-accent px-4 py-2 text-sm hover:bg-accent/20 transition-colors">
              <Sparkles className="w-4 h-4" /> Ask AI about this listing
            </button>
            <div className="mt-4 ml-2 inline-flex items-center gap-2">
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Pin to compare</span>
              {["A", "B"].map((s) => (
                <button key={s} type="button"
                  onClick={() => { pinToSlot?.(s, { item_type: "vehicle", item_id: v.id, title }); toast.success(`Pinned to ${s} — open Vaelith AI to compare A vs B`); }}
                  data-testid={`pin-to-${s.toLowerCase()}`}
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full border border-border text-sm font-mono-data font-semibold hover:border-accent/50 hover:text-accent transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
            <Stat label="Year" value={v.year} />
            <Stat label="Miles" value={v.miles ? Number(v.miles).toLocaleString() : "—"} />
            <Stat label="Days on Market" value={v.days_on_market} />
            <Stat label="MSRP" value={fmt(v.msrp)} />
            <Stat label="Engine" value={v.engine} />
            <Stat label="Transmission" value={v.transmission} />
            <Stat label="Drivetrain" value={v.drivetrain} />
            <Stat label="Fuel" value={v.fuel_type} />
            <Stat label="Body" value={v.body_type} />
            <Stat label="Doors" value={v.doors} />
            <Stat label="Exterior" value={v.exterior_color} />
            <Stat label="Interior" value={v.interior_color} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <CopyStat label="VIN" value={v.vin} testId="stat-vin" />
            <Stat label="Source" value={v.source} />
          </div>

          <section className="mt-10">
            <h2 className="font-display text-2xl font-light tracking-tight mb-1">{v.make} Median Price Trend</h2>
            <p className="text-muted-foreground text-sm mb-4 font-mono-data">Verified daily data · builds over time</p>
            {trendLocked ? (
              <div className="bg-surface border border-border rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="vehicle-trend-locked">
                <p className="text-sm text-muted-foreground">Market price trends are a Vaelith Pro feature.</p>
                <Link to="/pricing"><Button className="rounded-full" size="sm"><Lock className="w-4 h-4 mr-2" />Join Vaelith Membership</Button></Link>
              </div>
            ) : trendData.length >= 2 ? (
              <div className="bg-surface border border-border rounded-xl p-6 h-[260px]" data-testid="vehicle-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtM} width={60} />
                    <Tooltip formatter={(x) => fmt(x)} contentStyle={{ fontFamily: "JetBrains Mono", fontSize: 12, background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                    <Line type="monotone" dataKey="price" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : trendData.length === 1 ? (
              <div className="bg-surface border border-border rounded-xl p-6 font-mono-data text-sm" data-testid="vehicle-trend-single">
                Median {fmt(trendData[0].price)} recorded {trendData[0].date} · trend line appears after the next daily refresh.
              </div>
            ) : <VerifiedUnavailable testId="vehicle-trend-empty" note="No verified market trend recorded for this make yet." />}
          </section>
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-surface border border-border rounded-xl p-5 space-y-3">
            <WatchlistButton itemType="vehicle" itemId={v.id} data={{ title, price: v.price }} className="w-full" />
            <Button variant="outline" data-testid="copy-summary-button" className="rounded-full w-full"
              onClick={async () => { (await copyText(summary)) ? toast.success("Listing details copied") : toast.error("Could not copy"); }}>
              <Copy className="w-4 h-4 mr-2" />Copy listing details
            </Button>
            {marketUrl && (
              <Button variant="outline" data-testid="view-market-band-button" className="rounded-full w-full"
                onClick={() => navigate(marketUrl)} title={`See the ${vBand.label} band for ${v.make} ${v.model}`}>
                <BarChart3 className="w-4 h-4 mr-2" />View in Market Intelligence
              </Button>
            )}
          </div>

          <InvestmentValuation val={val} metricLabels={METRIC_LABELS} />

          <div className="bg-surface border border-border rounded-xl p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Dealership</div>
            {dealer.name ? (
              <div data-testid="dealer-block">
                <div className="font-display font-medium text-lg">{dealer.name}</div>
                {dealer.type && <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{dealer.type}</div>}
                <div className="text-sm text-muted-foreground mt-2">{[dealer.street, dealer.city, dealer.state].filter(Boolean).join(", ")}</div>
                {dealer.phone && <div className="text-sm text-muted-foreground mt-2 flex items-center gap-2" data-testid="dealer-phone"><Phone className="w-3.5 h-3.5 shrink-0" />{dealer.phone}</div>}
              </div>
            ) : <VerifiedUnavailable testId="dealer-unavailable" note="No verified dealer information." />}
          </div>

          <div className="bg-surface border border-border rounded-xl p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mb-3">Location</div>
            {dealer.latitude != null && dealer.longitude != null ? (
              <div data-testid="vehicle-location">
                <div className="font-mono-data text-sm">{Number(dealer.latitude).toFixed(5)}, {Number(dealer.longitude).toFixed(5)}</div>
              </div>
            ) : <VerifiedUnavailable note="No verified coordinates." />}
          </div>
        </aside>
      </div>
    </div>
  );
}
